/**
 * Story-detail callout for related ransomware leak-site activity (Plan B Plan 2 T19).
 *
 * Pure component — consumes `story.relatedLeakSiteActivity` (extended on
 * `StoryDetailShape` when T9 ships server-side enrichment). Renders only when
 * the activity array is non-empty; spec §5.5 requires the section to be
 * omitted entirely on empty (no zero-state widget).
 *
 * Spec: `2026-04-27-ransomware-leak-site-tracker-v1-design.md` §5.5.
 */

export interface RelatedLeakSiteActivity {
  groupName: string
  recentClaimCount: number
  lastActiveAt: string
}

export interface LeakSiteActivityCalloutProps {
  activity: RelatedLeakSiteActivity[]
}

export function LeakSiteActivityCallout({ activity }: LeakSiteActivityCalloutProps) {
  if (!activity || activity.length === 0) return null
  return (
    <section
      aria-labelledby="leak-site-activity-callout-heading"
      className="rounded-md border border-border bg-muted/30 p-4 text-sm"
    >
      <h3
        id="leak-site-activity-callout-heading"
        className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
      >
        Related ransomware activity
      </h3>
      <ul className="space-y-1.5">
        {activity.map((a) => {
          const lastActive = formatLastActive(a.lastActiveAt)
          const claimWord = a.recentClaimCount === 1 ? 'claim' : 'claims'
          return (
            <li key={a.groupName} className="flex flex-wrap items-baseline gap-x-2">
              <strong className="font-medium text-foreground">{a.groupName}</strong>
              <span className="text-muted-foreground">
                — {a.recentClaimCount} recent {claimWord}
                {lastActive ? <> · last active {lastActive}</> : null}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function formatLastActive(value: string): string | null {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString()
}
